import { ApiTax } from '@vend/types/tax'
import { SaleTax } from '../../../models/sale'
import { selectLineItems } from '../../../state/slices/currentSale/currentSaleSlice'
import { LineItemTaxComponent } from '../../../models/line-item'
import { useAppSelector } from '../../../state/hooks'

export type DisplaySaleTax = {
  id: string
  name: string
  isTaxGroup: boolean
  total: number
  rates: SaleTax[]
}

type TaxesById = Record<string, ApiTax>
type TaxComponentsById = Record<string, LineItemTaxComponent[]>

const getTotalRateForTax = (tax: ApiTax) =>
  tax.rates.reduce((total, rate) => total + rate.rate, 0)

/**
 * Hook to get taxes and rates in a sale.
 * Tax can be a tax group if it has more than 1 rates
 * If a tax is tax group, it will have field total which is total of all the tax rates
 * This hook is used in the sale tax popover at sale footer
 */
export const useDisplayTax = (): DisplaySaleTax[] => {
  const lineItems = useAppSelector(selectLineItems)

  const allTaxes: TaxesById = {}
  const allTaxComponents: TaxComponentsById = {}

  lineItems.forEach(lineItem => {
    // taxInfo contains information about tax group for the line item
    // tax components contains tax total per tax rate for the line item
    const { taxInfo, taxComponents } = lineItem
    if (taxInfo && taxComponents) {
      const taxId = taxInfo.id
      allTaxes[taxId] = taxInfo
      if (!allTaxComponents[taxId]) {
        allTaxComponents[taxId] = []
      }
      allTaxComponents[taxId].push(...taxComponents)
    }
  })

  const displayTaxes: DisplaySaleTax[] = []

  Object.values(allTaxes).forEach(tax => {
    // total tax of all rates
    let taxSubTotal = 0
    const ratesById: Record<string, SaleTax> = {}
    const taxComponents = allTaxComponents[tax.id]
    const removable = tax.rates.length < 3

    tax.rates.forEach(rate => {
      const taxTotals = taxComponents.filter(
        ({ rate_id }) => rate_id === rate.id
      )

      taxTotals.forEach(taxTotal => {
        if (!ratesById[rate.id]) {
          ratesById[rate.id] = {
            rate: taxTotal.rate,
            total: taxTotal.total_tax_after_sale_discount,
            removable,
            name: taxTotal.rate.name,
          }
        } else {
          ratesById[rate.id].total += taxTotal.total_tax_after_sale_discount
          ratesById[rate.id].removable = removable
        }

        taxSubTotal += taxTotal.total_tax_after_sale_discount
      })
    })
    // exclude No Tax by checking total > 0
    if (getTotalRateForTax(tax) > 0) {
      const rates = Object.values(ratesById) // convert dictionary to array
      displayTaxes.push({
        id: tax.id,
        name: tax.name,
        isTaxGroup: rates.length > 1,
        total: taxSubTotal,
        rates,
      })
    }
  })

  return displayTaxes
}
